import {
	GuideType,
	IdPCommonInputFieldName,
	IdPOidcInputFieldName,
	IdPSamlInputFieldName,
	IdPXaaInputFieldName,
	Method,
	SCIMMethod,
	SPOidcDataFieldName,
	SPSamlDataFieldName,
	SPScimDataFieldName,
	SPXaaDataFieldName
} from 'consts';

type IIdP = {
	id: string;
	name: string;
	logos: {
		light?: string;
		dark?: string;
	};
	methods: Method[];
	scimMethods?: SCIMMethod[];
};

export enum S4Feature {
	Sso = 'sso',
	Scim = 'scim',
	// Legacy, now split into RoleMapping + FgaMapping; kept as a fallback for
	// configs not yet migrated to the two flags.
	GroupMapping = 'groupMapping',
	RoleMapping = 'roleMapping',
	FgaMapping = 'fgaMapping',
	SsoDomains = 'ssoDomains',
	Oidc = 'oidc',
	Saml = 'saml',
	JitGuide = 'jitGuide',
	Xaa = 'xaa'
}
// Opt-out features are stored as `${feature}Disabled`, opt-in ones as `${feature}Enabled`.
// An opt-in feature cannot use a *Disabled key: protobuf drops false from this config's JSON,
// so absent and false would be indistinguishable and neither could mean "off".
export const S4_OPT_IN_FEATURES = [S4Feature.Xaa] as const;

export type S4OptInFeature = (typeof S4_OPT_IN_FEATURES)[number];
export type S4OptOutFeature = Exclude<S4Feature, S4OptInFeature>;

export type S4FeaturesDisabledKeys = `${S4OptOutFeature}Disabled`;
export type S4FeaturesEnabledKeys = `${S4OptInFeature}Enabled`;
export type S4FeaturesKeys = S4FeaturesDisabledKeys | S4FeaturesEnabledKeys;

export interface IS4Features extends Partial<Record<S4FeaturesKeys, boolean>> {}

export type IAppConfig = {
	styleId?: string;
	disableUserMappingCreate?: boolean;
	features?: IS4Features;
	forceDomainVerification?: boolean;
	supportEmail?: string;
	oidcLoginIdMappingDefaultOverride?: string;
	loginFlowId?: string;
	loginFlowStyleId?: string;
};

export type IGuideStep = {
	title: string;
	content: string;
	groupMapping?: boolean;
	attributeMapping?: boolean;
};

export interface IMethodGuide {
	guide: IGuideStep[];
	mapping?: {
		groupAttributeName?: string;
		userAttributesDefaults?: Record<string, string | null>;
	};
	defaults?: Record<string, string | null | string[] | boolean>;
}

export interface IResIdPGuide {
	id: string;
	guides: Partial<
		Record<GuideType.SSO, Partial<Record<`${Method}`, IMethodGuide>>>
	> &
		Partial<
			Record<GuideType.SCIM, Partial<Record<`${SCIMMethod}`, IMethodGuide>>>
		> &
		Partial<Record<GuideType.XAA, Partial<Record<`${Method}`, IMethodGuide>>>>;
}

export interface IReqVerifyToken {
	token: string;
	projectId: string;
}

export interface IResTokenExchange {}
export enum AuthSource {
	DSRSSO = 'dsrsso',
	Bearer = 'bearer',
	DSR = 'dsr'
}

export interface IResAuthorized {
	verified: boolean;
	done: boolean;
	projectId: string | null;
	tenantId: string | null;
	unauthorizedReason: UnauthorizedReason | null;
	domainVerificationTxt: string | null;
	authSource?: AuthSource | null;
}

export type { IIdP };
export interface IResIdPs {
	data: IIdP[];
	total: number;
}

export type IUserAttributeMapping = {
	idpAttr: string | null;
	spAttr: string | null;
};

export type IGroupsAttributeMapping = {
	group: string | null;
	roleId: string | null;
};

export interface ITenantConfig {
	authType: 'saml' | 'oidc' | 'none';
	projectID: string;
	id: string;
	name: string;
	domains?: string[];
	domainsToSSO?: Record<string, string>;
	SSOWrapperMap?: Record<
		string,
		{
			samlId: string;
			oauthId: string;
			authType: 'saml' | 'oidc' | 'none';
			displayName: string;
		}
	>;
	JITDisabled?: boolean;
	JITDisabledSSO?: Record<string, boolean>;
}

// spAttr: idpAttr
export type IConfigMapping = {
	customAttributes?: Record<string, string>;
} & Record<Exclude<string, 'customAttributes'>, string>;

// Cross-App Access (XAA) per-issuer TRUST settings, stored under
// xaa.jwtBearerSettings.issuers keyed by issuer URL. attributeMapping mirrors SAML's
// samlMappings (IConfigMapping). Config-level mapping (group->role, default roles, FGA)
// lives on the top-level IXaaConfig alongside the issuers.
export interface IXaaIssuerSettings {
	jwksUri?: string;
	signAlgorithm?: string;
	userInfoUri?: string;
	externalIdFieldName?: string;
	jitDisabled?: boolean;
	attributeMapping?: IConfigMapping;
}

// Cross-App Access (XAA / ID-JAG) top-level config, returned by GET /v1/sso and accepted
// by POST /v1/sso alongside saml/oauth/tenant. Holds the trusted issuers
// (jwtBearerSettings.issuers) and the config-level mapping; the tenant no longer carries
// any XAA trust. The mapping fields (samlGroupsMappings / defaultSSORoles /
// rebacGroupsMappings) mirror ISamlConfig exactly. version is an int64 serialized as a
// JSON string by the gateway.
export interface IXaaConfig {
	// See ISamlConfig.authenticationOnly - all three rows carry the same value.
	authenticationOnly?: boolean;
	providerID?: string;
	enabled?: boolean;
	id?: string;
	version?: string;
	additional?: boolean;
	jwtBearerSettings?: {
		issuers?: Record<string, IXaaIssuerSettings>;
	};
	samlGroupsMappings?: {
		role?: { id?: string; name?: string };
		groups?: string[];
	}[];
	defaultSSORoles?: string[];
	rebacGroupsMappings?: Record<string, IReBACGroupMappings> | null;
	groupsPriority?: Record<string, number>;
	groupPriorityEnabled?: boolean;
	allowOverrideRoles?: boolean;
}

export interface ISamlConfig {
	// Verify identity only: a login through this connection creates no user.
	authenticationOnly?: boolean;
	spCert: string;
	signRequest: boolean;
	samlMappings: IConfigMapping;
	id: string;
	version: number;
	mergeUsers: boolean;
	signRequestCert: string;
	acsUrl: string;
	xmlMetadataUrl: string;
	scimUrl: string;
	isSCIMKeyGenerated?: boolean;
	descopeEntityID: string;

	providerID?: string;
	idpURL?: string;
	idpCert?: string;
	entityID?: string;
	enabled?: boolean;
	useMetaInfoToggle?: boolean;
	idpMetadataURL?: string;
	scimProviderID?: string;
	lastSuccessTestTime?: number;
	lockedTenantId?: string;

	samlGroupsMappings?: {
		role: { id: string };
		groups: string[];
	}[];
	mandatoryUserAttributes?: string[];
	defaultSSORoles?: string[] | null;
	rebacGroupsMappings?: Record<string, IReBACGroupMappings> | null;
}

export interface IOAuthConfig {
	providerSettings: Record<
		string,
		{
			// See ISamlConfig.authenticationOnly - all three rows carry the same value. On the OIDC
			// side it lives here, per tenant, not on the wrapper above.
			authenticationOnly?: boolean;
			custom: boolean;
			useSelfAccount: boolean;
			scopeDelimiter: string;
			SignAlgorithm: string;
			userDataAuthHeaderName: string;
			userDataAuthHeaderType: string;
			userDataClaimsMapping: IConfigMapping;
			trustProvidedEmails: boolean;
			nonce: boolean;

			providerID?: string;
			name?: string;
			enabled?: boolean;
			clientID?: string;
			clientSecret?: string;
			authURL?: string;
			tokenURL?: string;
			userDataURL?: string;
			scope?: string[];
			prompt?: string[];
			JWKsURI?: string;
			grantType?: string;
			issuer?: string;
			usePkce?: boolean;

			scimProviderID?: string;
			lastSuccessTestTime?: number;

			oidcGroupsMappings?: {
				role: { id: string };
				groups: string[];
			}[];
			defaultSSORoles?: string[] | null;
			rebacGroupsMappings?: Record<string, IReBACGroupMappings> | null;
		}
	>;
	id: string;
	version: number;
	callbackUrl: string;
	scimUrl: string;
	isSCIMKeyGenerated?: boolean;
	lockedTenantId?: string;

	enabled?: boolean;
	ssoMergeUsers?: boolean;
	mandatoryUserAttributes?: string[];
}

export interface IResConfigs {
	currentSSOId: string;
	tenant: ITenantConfig;
	saml: ISamlConfig;
	oauth: IOAuthConfig;
	// Cross-App Access top-level config: the trusted issuers and the config-level XAA
	// mapping. The tenant carries no XAA trust.
	xaa?: IXaaConfig;
	// The project-level ID-JAG audience, computed by the backend so it follows the
	// project custom domain. It names no tenant - the tenant travels in the assertion's
	// aud_tenant claim. Absent on older backends - see deriveXaaAudience.
	xaaAudience?: string;
}

export interface IResSP {
	acsUrl: string;
	entityID: string;
	metadataUrl: string;
}

export interface IResAttribute {
	label: string;
	value: string;
	custom?: boolean;
}

export type IResUserAttributes = IResAttribute[];

export interface IUserAttribute extends IResAttribute {
	required?: boolean;
}
export type IUserAttributes = IUserAttribute[];

export interface IRole {
	id: string;
	name: string;
	description?: string;
}

export type IResRoles = IRole[];

export interface IReBACGroupMapping {
	resource: string;
	relationDefinition: string;
	namespace: string;
}

export interface IReBACGroupMappings {
	relations: IReBACGroupMapping[];
}

export interface IFGAGroupMapping {
	id: string;
	group: string;
	fgaType: string;
	relation: string;
	resource: string;
}

export interface IMethodConfigFields {
	// SAML:
	[IdPSamlInputFieldName.IdPSsoUrl]?: string | null;
	[IdPSamlInputFieldName.IdPIssuer]?: string | null;
	[IdPSamlInputFieldName.IdPCertificate]?: string | null;
	[IdPSamlInputFieldName.IdPMetadataUrl]?: string | null;

	// OIDC:
	[IdPOidcInputFieldName.ProviderName]?: string | null;
	[IdPOidcInputFieldName.ClientId]?: string | null;
	[IdPOidcInputFieldName.ClientSecret]?: string | null;
	[IdPOidcInputFieldName.Scopes]?: string[] | null;
	[IdPOidcInputFieldName.GrantType]?: string | null;
	[IdPOidcInputFieldName.Issuer]?: string | null;
	[IdPOidcInputFieldName.AuthorizationEndpoint]?: string | null;
	[IdPOidcInputFieldName.TokenEndpoint]?: string | null;
	[IdPOidcInputFieldName.UserInfoEndpoint]?: string | null;
	[IdPOidcInputFieldName.JWKsEndpoint]?: string | null;
	[IdPOidcInputFieldName.Prompt]?: string[] | null;
	[IdPOidcInputFieldName.UsePkce]?: boolean | null;

	// XAA (Cross-App Access / ID-JAG):
	[IdPXaaInputFieldName.Issuer]?: string | null;
	[IdPXaaInputFieldName.JwksUri]?: string | null;
	[IdPXaaInputFieldName.SignAlgorithm]?: string | null;
	[IdPXaaInputFieldName.UserInfoUri]?: string | null;
	[IdPXaaInputFieldName.Jit]?: string | null;

	// Common:
	[IdPCommonInputFieldName.SSODomains]?: string[] | null;
	[IdPCommonInputFieldName.UserAttributes]?: IUserAttributeMapping[] | null;
	[IdPCommonInputFieldName.GroupsAttribute]?: IUserAttributeMapping | null;
	[IdPCommonInputFieldName.GroupsAttributeMapping]?:
		IGroupsAttributeMapping[] | null;
	[IdPCommonInputFieldName.DefaultSSORoles]?: string[] | null;
	[IdPCommonInputFieldName.RebacGroupsMappings]?: Record<
		string,
		IReBACGroupMappings
	> | null;

	// these should always be empty, they are here for type assertions only
	[SPSamlDataFieldName.SpAcsUrl]?: string | null;
	[SPSamlDataFieldName.SpEntityID]?: string | null;
	[SPSamlDataFieldName.SpMetadataUrl]?: string | null;
	[SPScimDataFieldName.SCIMURL]?: string | null;
	[SPScimDataFieldName.SCIMToken]?: string | null;
	[SPOidcDataFieldName.CallbackUrl]?: string | null;
	[SPXaaDataFieldName.Audience]?: string | null;
	[SPXaaDataFieldName.TenantId]?: string | null;
}

export type ISSOConfigFields = Record<
	string, // idpId
	| Record<
			string, // Method, using the actual enum weakens type checking
			IMethodConfigFields | undefined
	  >
	| undefined
>;

export interface IDPResponse {
	samlResponse: string;
	samlGeneratedUser: string;
	samlGeneratedRoles: string;
	oidcResponse: string;
	oidcGeneratedUser: string;
	oidcGeneratedRoles: string;
}

export type IErrorResponse = {
	errorCode: string;
	errorDescription: string;
	errorMessage: string;
	message: string;
};

export enum UnauthorizedReason {
	CONFIG_FETCH_FAILED = 'config_fetch_failed', // Failed to load required app config from server
	NO_AUTH = 'no_auth', // No authentication present, (no jwt or token); default fallback reason
	LINK_EXPIRED = 'link_expired', // Access link token has expired
	MISSING_PERMISSIONS = 'missing_permissions', // Authenticated but lacks necessary permissions
	COULD_NOT_SELECT = 'could_not_select', // Could not select the tenant from multiple possibilities
	INVALID_USER = 'invalid_user' // Invalid user, (user not found or expected temp user)
}

export type IUnauthorizedResponse = {
	unauthorizedReason?: UnauthorizedReason;
};

export type ITypographyVariant =
	'h1' | 'h2' | 'h3' | 'subtitle1' | 'subtitle2' | 'body1' | 'body2' | 'button';

export interface IResThemeTypography {
	size?: string;
	weight?: string;
	font?: string;
}

export interface IResThemeMode {
	type?: 'ssoSuite';
	name?: string;
	components?: {
		logo?: {
			'--descope-logo-url'?: string;
			favicon?: string;
		};
	};
	globals?: {
		colors?: {
			primary: {
				contrast: string;
				dark: string;
				highlight: string;
				light: string;
				main: string;
			};
		};
		fonts?: {
			font1?: {
				family: string[];
				label: string;
				url?: string;
			};
			font2?: {
				family: string[];
				label: string;
				url?: string;
			};
		};
		typography?: Partial<Record<ITypographyVariant, IResThemeTypography>>;
	};
}

export type IAppThemeTypography = {
	size: string;
	weight: string;
	font: 'font1' | 'font2';
};

export type IResTheme = {
	dark: IResThemeMode;
	light: IResThemeMode;
};

export interface IFGAMappableSchemaResponse {
	fgaAvailable?: boolean;
	mappableResources?: IMappableResources[];
	// Both sent only for the console project.
	customRoleDefinitions?: ICustomRoleDefinition[];
	// The mappable types a custom role may be scoped to; composition is restricted to these.
	customRoleScopeTypes?: string[];
}

// A custom role is offered next to the built-in roles of its scope's type. The scoped resource it
// is stored against is composed on the client from the chosen scope and this slug.
export interface ICustomRoleDefinition {
	roleId: string;
	roleName: string;
	slug: string;
}

export interface IMappableResources {
	type: string;
	// Both omitted by the backend (omitempty) when empty, so both arrive undefined.
	relations?: IRelationOption[];
	resources?: IMappableResource[];
}

// displayName overrides the label; absent leaves it derived from the relation name.
export interface IRelationOption {
	relation: string;
	displayName?: string;
}

export interface IMappableResource {
	resource: string;
	displayName: string;
}

export interface IVerifyDomainRes {
	verified: boolean;
}

export interface IDnsProvider {
	id: string;
	name: string;
	logos?: { light?: string; dark?: string };
	nsPatterns?: string[];
	guide: IGuideStep[];
}

export interface IResDnsProviders {
	providers: IDnsProvider[];
}

export interface IResNsServers {
	servers: string[] | null;
}

export interface IScimKeyRequest {
	ssoId?: string;
}

export interface IScimKeyResponse {
	cleartext: string;
	key: unknown;
}
